import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from '../../components/Button';
import { OfferingType } from '../../utils/offeringType';
import { ApiRequest } from '../../utils/api';
import { useApiQuery } from '../../utils/apiHooks';
import { B2B2ProductCards } from './B2B2ProductCards';
import { B2B2OfferingChange } from './B2B2OfferingChange';
import { getAccountId } from '../../utils/helpers';
import { Order } from '../../model/Order';
import { Error } from '../../components/Error';
import _ from 'lodash';

interface Props {
	currentPrice: number;
	currentOrder: Order;
	continueToCurrentOrder: () => void;
	offeringType: OfferingType;
}

export const B2B2SalesArguments: React.FC<Props> = (props: Props) => {
	const { data: targetProducts } = useApiQuery(ApiRequest.getProducts(OfferingType.B2B2), { cacheTime: 60000 });
	const { data: activeSubscriptions } = useApiQuery(ApiRequest.getSims(getAccountId(), true, true));
	const { data: nonActivatedSubscriptions } = useApiQuery(ApiRequest.getNonActivatedSims(getAccountId(), true));
	const { data: emptySims } = useApiQuery(ApiRequest.getEmptySims(getAccountId()));

	const [showOfferingChangeView, setShowOfferingChangeView] = useState(false);
	const [submitResult, setSubmitResult] = useState<boolean | undefined>(undefined);

	const hasActiveSubs = activeSubscriptions && activeSubscriptions.length > 0;
	const hasNonActiveSubs = nonActivatedSubscriptions && nonActivatedSubscriptions.length > 0;
	const hasEmptySims = emptySims && emptySims.length > 0;

	const dataLoaded = targetProducts && activeSubscriptions && nonActivatedSubscriptions && emptySims;

	const getSalesArguments = () => {
		return (
			<SalesArguments>
				<h2>Päivitä liittymäsi</h2>
				<h3 className="h4">Moin Bisnes-liittymissä on Euroopan reiluimmat puhe- ja tekstarihinnat</h3>
				<p>
					Euroopan reiluimmat liittymät* ovat saatavilla myös yrityksellesi. Kunkin liittymän puhelut ja tekstarit aina enintään 4 €/kk.** Liittymien päivittäminen onnistuu kerralla, parissa minuutissa. Kun päivität liittymäsi, uudet liittymät ovat käytössä seuraavan laskutuskautesi vaihteessa. Sinun ei tarvitse edes vaihtaa olemassa olevia Moin SIM-kortteja laitteisiisi, vaan nykyiset SIM-korttisi toimivat edelleen.
				</p>
				<p>Puhelut ja tekstarit Suomessa ja EU-maissa yhteensä enintään 4 €/kk. Maksat puheluista ja tekstareista käytön mukaan 0,044 €/min tai kpl, et kuitenkaan koskaan enempää kuin 4€/kk.</p>
				<p>
					*Moin puhelut ja tekstarit kuuluvat Euroopan reiluimmin hinnoiteltujen mobiilipalvelujen joukkoon. Eläköön reiluus.<br />
					**Ei koske Bisnes Laitenettiä.{' '}
					<a href="https://www.moi.fi/yrityksille/hinnasto/" target="_blank" rel="noreferrer">
						Katso Bisnes Laitenetin hinnasto.
					</a>
				</p>
			</SalesArguments>
		);
	};
	if (submitResult !== undefined && !submitResult) {
		return <Error message={'Jotain meni vikaan. Yritä myöhemmin uudestaan tai ota yhteyttä asiakaspalveluumme'} />;
	} else if (activeSubscriptions && nonActivatedSubscriptions && targetProducts && showOfferingChangeView) {
		return (
			<B2B2OfferingChange
				currentPrice={props.currentPrice}
				currentOrder={props.currentOrder}
				subscriptions={_.uniqBy(activeSubscriptions.concat(nonActivatedSubscriptions), 'subscriptionNumber')}
				targetProducts={targetProducts.productPackagePrices}
				continueToCurrentOrder={props.continueToCurrentOrder}
				onSubmit={(success: boolean) => setSubmitResult(success)}
			/>
		);
	} else if (dataLoaded && (hasActiveSubs || hasNonActiveSubs)) {
		return (
			<PageWrapper>
				{getSalesArguments()}
				<UpdateDealConfirmation>
					<h4>Haluatko tutustua uusiin Moi Bisnes-liittymiin ja päivittää nykyisen sopimuksesi?</h4>
					<div>
						<Button onClick={() => setShowOfferingChangeView(true)}>
							Kyllä. Tutustun liittymiin ja päivitän sopimukseni...
						</Button>
					</div>
					<div>
						<Button className="invisible" onClick={props.continueToCurrentOrder}>
							Ei kiitos. Jatkan nykyisellä sopimuksella.
						</Button>
					</div>
				</UpdateDealConfirmation>
				<h4 className="b2b-packages-title">Moi Bisnespaketit</h4>
				{targetProducts && <B2B2ProductCards targetProducts={targetProducts.productPackagePrices} />}
				<UpdateDealConfirmation>
					<h4>Haluatko tutustua uusiin Moi Bisnes-liittymiin ja päivittää nykyisen sopimuksesi?</h4>
					<div>
						<Button onClick={() => setShowOfferingChangeView(true)}>
							Kyllä. Tutustun liittymiin ja päivitän sopimukseni...
						</Button>
					</div>
					<div>
						<Button className="invisible" onClick={props.continueToCurrentOrder}>
							Ei kiitos. Jatkan nykyisellä sopimuksella.
						</Button>
					</div>
				</UpdateDealConfirmation>
			</PageWrapper>
		);
	} else if (dataLoaded && !hasActiveSubs && !hasNonActiveSubs && hasEmptySims) {
		return (
			<PageWrapper>
				<SalesArguments>
					<h2>Päivitä liittymäsi</h2>
					<h3 className="h4">Moin Bisnes-liittymissä on Euroopan reiluimmat puhe- ja tekstarihinnat</h3>
					<p>
						Euroopan reiluimmat liittymät* ovat saatavilla myös yrityksellesi. Kunkin liittymän puhelut ja tekstarit aina enintään 4 €/kk.**
					</p>
					<p>
						Koska tililläsi ei ole vielä liittymiä, sopimuksen päivittäminen voidaan tehdä heti! Näppärää, eikö?
					</p>
					<p>Puhelut ja tekstarit Suomessa ja EU-maissa yhteensä enintään 4 €/kk. Maksat puheluista ja tekstareista käytön mukaan 0,044 €/min tai kpl, et kuitenkaan koskaan enempää kuin 4€/kk.</p>

					<p>
						*Moin puhelut ja tekstarit kuuluvat Euroopan reiluimmin hinnoiteltujen mobiilipalvelujen joukkoon. Eläköön reiluus.<br />
						**Ei koske Bisnes Laitenettiä.{' '}
						<a href="https://www.moi.fi/yrityksille/hinnasto/" target="_blank" rel="noreferrer">
							Katso Bisnes Laitenetin hinnasto.
						</a>
					</p>
				</SalesArguments>
				<UpdateDealConfirmation>
					<h4>Haluatko tutustua uusiin Moi Bisnes-liittymiin ja päivittää nykyisen sopimuksesi?</h4>
					<div>
						<Button onClick={() => setShowOfferingChangeView(true)}>Kyllä. Tutustun liittymiin ja päivitän sopimukseni...</Button>
					</div>
					<div>
						<Button className="invisible" onClick={props.continueToCurrentOrder}>
							Ei kiitos. Jatkan nykyisellä sopimuksella.
						</Button>
					</div>
				</UpdateDealConfirmation>
				<h4 className="b2b-packages-title">Moi Bisnespaketit</h4>
				{targetProducts && <B2B2ProductCards targetProducts={targetProducts.productPackagePrices} />}
				<UpdateDealConfirmation>
					<h4>Haluatko tutustua uusiin Moi Bisnes-liittymiin ja päivittää nykyisen sopimuksesi?</h4>
					<div>
						<Button onClick={() => setShowOfferingChangeView(true)}>Kyllä. Tutustun liittymiin ja päivitän sopimukseni...</Button>
					</div>
					<div>
						<Button className="invisible" onClick={props.continueToCurrentOrder}>
							Ei kiitos. Jatkan nykyisellä sopimuksella.
						</Button>
					</div>
				</UpdateDealConfirmation>
			</PageWrapper>
		)
	} else if (dataLoaded && !hasActiveSubs && !hasNonActiveSubs && !hasEmptySims) {
		return (
			<PageWrapper>
				<SalesArguments>
					<h2>Päivitä liittymäsi</h2>
					<p className="lg-text text-center">Tililläsi ei ole tällä hetkellä aktiivisia liittymiä eikä vapaita SIM-kortteja. Valitettavasti tilin  päivittäminen ei ole tämän vuoksi mahdollista.</p>
				</SalesArguments>
				<UpdateDealConfirmation>
					<div>
						<Button onClick={props.continueToCurrentOrder}>
							OK
						</Button>
					</div>
				</UpdateDealConfirmation>
			</PageWrapper>
		)
	}
	return <React.Fragment />;
};

const PageWrapper = styled.div`
	h4,
	.h4 {
		font-size: 1.364rem;
	}

	.b2b-packages-title {
		margin: 0 0 1.5rem;
		text-align: center;
	}

	@media screen and (min-width: 40em) {
		h4,
		.h4 {
			font-size: 1.818rem;
		}
		.b2b-packages-title {
			margin: 0 0 1.5rem;
		}
	}
`;

const SalesArguments = styled.div`
	max-width: 900px;
	margin: 0 auto 1.5rem;
	h2 {
		text-align: center;
		font-size: 1.818rem;
	}
	h3 {
		line-height: 1.2;
	}
	p {
		margin: 0 0 1rem;
		
		&.lg-text {
			font-size: 20px;
		}

		a {
			color: #333;
			text-decoration: underline;

			&:hover,
			&:focus {
				color: #ff0000;
			}
		}
	}

	@media screen and (min-width: 40em) {
		margin-bottom: 2.5rem;

		h2 {
			font-size: 2.667rem;
		}
		h4 {
			font-size: 1.364rem;
		}
		p {
			&.lg-text {
				font-size: 24px;
			}
		}
	}
`;

const UpdateDealConfirmation = styled.div`
	text-align: center;
	margin-bottom: 2rem;
	h4 {
		font-size: 1.136rem;
	}
	@media screen and (min-width: 40em) {
		margin-bottom: 3rem;
		h4 {
			font-size: 1.364rem;
		}
		button {
			min-width: 360px;
		}
	}
`;
